import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  ShoppingCart,
  Search,
  Package,
  CreditCard,
  Star,
  Truck,
  ShieldCheck,
  Filter,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DropshippingStore = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [demoMode, setDemoMode] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/payments/store/products`);
      const data = await response.json();
      
      if (data.success) {
        setProducts(data.products || []);
        setDemoMode(data.demo_mode || false);
      } else {
        toast.error('Failed to load products');
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Error loading store products');
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (product, price) => {
    const existingItem = cart.find(item => item.priceId === price.id);
    
    if (existingItem) {
      setCart(cart.map(item =>
        item.priceId === price.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
      toast.success('Quantity updated in cart');
    } else {
      setCart([...cart, {
        productId: product.id,
        priceId: price.id,
        name: product.name,
        price: price.amount / 100,
        currency: price.currency,
        image: product.images[0] || null,
        quantity: 1
      }]);
      toast.success('Added to cart');
    }
  };

  const removeFromCart = (priceId) => {
    setCart(cart.filter(item => item.priceId !== priceId));
    toast.success('Removed from cart');
  };

  const updateQuantity = (priceId, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(priceId);
      return;
    }
    setCart(cart.map(item =>
      item.priceId === priceId
        ? { ...item, quantity: newQuantity }
        : item
    ));
  };

  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    if (demoMode) {
      toast.info('Demo mode - checkout disabled. Add valid Stripe API key to enable purchases.');
      return;
    }

    try {
      // For simplicity, checkout first item (extend for multi-item checkout)
      const firstItem = cart[0];
      
      const response = await fetch(`${API_URL}/api/payments/store/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          price_id: firstItem.priceId,
          quantity: firstItem.quantity,
          success_url: `${window.location.origin}/?checkout=success`,
          cancel_url: `${window.location.origin}/?checkout=cancel`,
          metadata: {
            source: 'nexus_dropshipping_store'
          }
        })
      });

      const data = await response.json();
      
      if (data.success && data.url) {
        // Redirect to Stripe checkout
        window.location.href = data.url;
      } else {
        toast.error(data.error || 'Checkout failed');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to create checkout session');
    }
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="space-y-6">
      {/* Demo Mode Banner */}
      {demoMode && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 flex items-center gap-3">
          <ShieldCheck className="w-5 h-5 text-yellow-500" />
          <div>
            <p className="text-yellow-500 font-semibold">Demo Mode Active</p>
            <p className="text-yellow-400 text-sm">
              Showing demo products. Add a valid Stripe API key in your dashboard to display real products and enable purchases.
            </p>
          </div>
        </div>
      )}

      {/* Store Header */}
      <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 rounded-2xl p-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <Package className="w-8 h-8" />
              <h1 className="text-3xl font-bold">Dropshipping Store</h1>
            </div>
            <p className="text-purple-100 text-lg">Premium products powered by Stripe</p>
          </div>
          <div className="relative">
            <Button
              onClick={() => setShowCart(!showCart)}
              className="bg-white text-purple-600 hover:bg-purple-50 px-6 py-3 text-lg font-semibold"
            >
              <ShoppingCart className="w-5 h-5 mr-2" />
              Cart ({cartCount})
            </Button>
            {cartCount > 0 && (
              <Badge className="absolute -top-2 -right-2 bg-red-500 text-white">
                {cartCount}
              </Badge>
            )}
          </div>
        </div>

        {/* Trust Badges */}
        <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-white/20">
          <div className="flex items-center gap-2">
            <Truck className="w-5 h-5" />
            <span className="text-sm">Fast Shipping</span>
          </div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" />
            <span className="text-sm">Secure Payment</span>
          </div>
          <div className="flex items-center gap-2">
            <Star className="w-5 h-5" />
            <span className="text-sm">Quality Guaranteed</span>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        <Input
          type="text"
          placeholder="Search products..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-12 py-6 bg-[#1a1a22] border-purple-500/20 text-white placeholder:text-gray-400 text-lg"
        />
      </div>

      {/* Shopping Cart Sidebar */}
      {showCart && (
        <Card className="fixed right-4 top-20 w-96 max-h-[80vh] overflow-auto z-50 bg-[#1a1a22] border-purple-500/20 shadow-2xl">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <ShoppingCart className="w-5 h-5" />
                Your Cart
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCart(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {cart.length === 0 ? (
              <p className="text-gray-400 text-center py-8">Your cart is empty</p>
            ) : (
              <>
                <div className="space-y-4 mb-6">
                  {cart.map((item) => (
                    <div key={item.priceId} className="flex gap-3 pb-4 border-b border-gray-700">
                      {item.image && (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-16 h-16 object-cover rounded"
                        />
                      )}
                      <div className="flex-1">
                        <p className="text-white font-medium text-sm">{item.name}</p>
                        <p className="text-purple-400 font-semibold">
                          ${item.price.toFixed(2)} {item.currency}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 w-6 p-0"
                            onClick={() => updateQuantity(item.priceId, item.quantity - 1)}
                          >
                            -
                          </Button>
                          <span className="text-white text-sm">{item.quantity}</span>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 w-6 p-0"
                            onClick={() => updateQuantity(item.priceId, item.quantity + 1)}
                          >
                            +
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="ml-auto text-red-400 hover:text-red-300"
                            onClick={() => removeFromCart(item.priceId)}
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-gray-700 pt-4">
                  <div className="flex justify-between text-lg font-bold text-white mb-4">
                    <span>Total:</span>
                    <span className="text-purple-400">${cartTotal.toFixed(2)}</span>
                  </div>
                  <Button
                    onClick={handleCheckout}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold py-3"
                  >
                    <CreditCard className="w-5 h-5 mr-2" />
                    Proceed to Checkout
                  </Button>
                </div>
              </>
            )}
          </div>
        </Card>
      )}

      {/* Products Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i} className="bg-[#1a1a22] border-purple-500/20 p-6 animate-pulse">
              <div className="bg-gray-700 h-48 rounded-lg mb-4"></div>
              <div className="bg-gray-700 h-6 rounded mb-2"></div>
              <div className="bg-gray-700 h-4 rounded w-3/4"></div>
            </Card>
          ))}
        </div>
      ) : filteredProducts.length === 0 ? (
        <Card className="bg-[#1a1a22] border-purple-500/20 p-12 text-center">
          <Package className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">No Products Available</h3>
          <p className="text-gray-400">
            {searchTerm
              ? 'No products match your search'
              : 'Add products in your Stripe dashboard to get started'}
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map((product) => (
            <Card
              key={product.id}
              className="bg-[#1a1a22] border-purple-500/20 hover:border-purple-500/40 transition-all duration-200 overflow-hidden group"
            >
              {/* Product Image */}
              {product.images && product.images[0] && (
                <div className="relative h-48 overflow-hidden bg-gray-800">
                  <img
                    src={product.images[0]}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  {product.active && (
                    <Badge className="absolute top-3 right-3 bg-green-500 text-white">
                      Available
                    </Badge>
                  )}
                </div>
              )}

              <div className="p-6">
                {/* Product Name */}
                <h3 className="text-xl font-bold text-white mb-2 line-clamp-2">
                  {product.name}
                </h3>

                {/* Description */}
                {product.description && (
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                    {product.description}
                  </p>
                )}

                {/* Prices */}
                <div className="space-y-2 mb-4">
                  {product.prices.map((price) => (
                    <div
                      key={price.id}
                      className="flex items-center justify-between p-3 bg-purple-500/10 rounded-lg border border-purple-500/20"
                    >
                      <div>
                        <p className="text-purple-400 font-bold text-lg">
                          ${(price.amount / 100).toFixed(2)} {price.currency}
                        </p>
                        {price.recurring && (
                          <p className="text-gray-500 text-xs">per {price.interval}</p>
                        )}
                      </div>
                      <Button
                        onClick={() => addToCart(product, price)}
                        size="sm"
                        className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                      >
                        <ShoppingCart className="w-4 h-4 mr-1" />
                        Add
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default DropshippingStore;
